/**
 * Settings — preferências e integrações da organização.
 */
import { Bell, Globe, Mail, Phone, Radio, ShieldCheck, Webhook, Zap } from "lucide-react";
import { Link } from "react-router-dom";

import { PageHeader } from "@/components/page-header";
import { useTheme } from "@/components/theme-provider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export function SettingsPage() {
  const { theme, setTheme } = useTheme();

  return (
    <>
      <PageHeader
        title="Configurações"
        description="Preferências da sua conta, integrações e equipe."
      />

      <Tabs defaultValue="preferences">
        <TabsList>
          <TabsTrigger value="preferences">Preferências</TabsTrigger>
          <TabsTrigger value="integrations">Integrações</TabsTrigger>
          <TabsTrigger value="team">Equipe</TabsTrigger>
          <TabsTrigger value="api">API</TabsTrigger>
        </TabsList>

        <TabsContent value="preferences" className="space-y-4">
          <SettingsCard title="Aparência" description="Tema da interface">
            <div className="grid grid-cols-3 gap-3 max-w-md">
              {(["light", "dark", "system"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTheme(t)}
                  className={cn(
                    "rounded-xl border-2 p-3 text-sm font-medium transition-all",
                    theme === t
                      ? "border-primary-500 bg-primary-50/50 dark:bg-primary-500/10"
                      : "border-slate-200 dark:border-white/10 hover:border-slate-300",
                  )}
                >
                  {t === "light" ? "Claro" : t === "dark" ? "Escuro" : "Sistema"}
                </button>
              ))}
            </div>
          </SettingsCard>

          <SettingsCard title="Notificações" description="Como você recebe avisos da plataforma">
            <div className="space-y-2 max-w-md">
              <NotifRow icon={Mail} label="Email" enabled />
              <NotifRow icon={Phone} label="WhatsApp" disabled hint="Em breve" />
              <NotifRow icon={Bell} label="Push (browser)" disabled hint="Em breve" />
            </div>
          </SettingsCard>
        </TabsContent>

        <TabsContent value="integrations" className="space-y-4">
          <IntegrationCard
            icon={Phone}
            name="WhatsApp Business"
            description="Notificar técnicos e clientes via WhatsApp"
            status="disconnected"
            actionLabel="Conectar"
          />
          <IntegrationCard
            icon={Zap}
            name="Loopi (antecipação de NF)"
            description="Antecipação de recebíveis dos prestadores"
            status="not-configured"
            actionLabel="Configurar"
          />
          <IntegrationCard
            icon={Globe}
            name="Google Maps"
            description="Geocodificação e cálculo de rota"
            status="connected"
            actionLabel="Gerenciar"
          />
          <IntegrationCard
            icon={Webhook}
            name="Webhooks"
            description="Eventos para sistemas externos (job.created, invoice.paid...)"
            status="not-configured"
            actionLabel="Configurar"
          />
        </TabsContent>

        <TabsContent value="team" className="space-y-4">
          <SettingsCard title="Membros da equipe" description="Usuários com acesso ao painel">
            <p className="text-sm text-muted-foreground mb-3">
              Adicione admins, operadores e financeiros. Técnicos e clientes acessam via fluxo de onboarding.
            </p>
            <Button asChild>
              <Link to="/users">Gerenciar usuários</Link>
            </Button>
          </SettingsCard>
          <SettingsCard title="Papéis disponíveis">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-2xl">
              <RoleCard role="super_admin" desc="Acesso total" />
              <RoleCard role="admin" desc="Gestão operacional" />
              <RoleCard role="operator" desc="Dispatch + chamados" />
              <RoleCard role="financial" desc="Faturamento" />
              <RoleCard role="client" desc="Abre chamados" />
              <RoleCard role="nerd" desc="Atende chamados" />
            </div>
          </SettingsCard>
        </TabsContent>

        <TabsContent value="api" className="space-y-4">
          <SettingsCard title="API REST" description="Endpoint pra integração programática">
            <div className="rounded-xl border border-slate-200/60 dark:border-white/10 bg-slate-50/40 dark:bg-white/[0.02] p-3 text-sm font-mono">
              https://api-one.encontreumnerd.com.br
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Autenticação via JWT (sign-in com Firebase). 97 endpoints REST disponíveis.
            </p>
            <Button variant="outline" size="sm" className="mt-3" asChild>
              <a href="https://api-one.encontreumnerd.com.br/docs" target="_blank" rel="noreferrer">
                Documentação OpenAPI
              </a>
            </Button>
          </SettingsCard>
        </TabsContent>
      </Tabs>
    </>
  );
}

function SettingsCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-6">
      <div className="mb-4">
        <h3 className="font-semibold text-base">{title}</h3>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

function NotifRow({
  icon: Icon,
  label,
  enabled,
  disabled,
  hint,
}: {
  icon: typeof Mail;
  label: string;
  enabled?: boolean;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{label}</span>
        {hint && <Badge variant="muted" size="sm">{hint}</Badge>}
      </div>
      <input
        type="checkbox"
        defaultChecked={enabled}
        disabled={disabled}
        className="h-4 w-4 rounded border-slate-300"
      />
    </div>
  );
}

function IntegrationCard({
  icon: Icon,
  name,
  description,
  status,
  actionLabel,
}: {
  icon: typeof Phone;
  name: string;
  description: string;
  status: "connected" | "disconnected" | "not-configured";
  actionLabel: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-5 flex items-center gap-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 dark:bg-white/5 shrink-0">
        <Icon className="h-5 w-5 text-slate-700 dark:text-slate-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-sm">{name}</h4>
          {status === "connected" ? (
            <Badge variant="success" dot>Conectado</Badge>
          ) : status === "disconnected" ? (
            <Badge variant="warning" dot>Desconectado</Badge>
          ) : (
            <Badge variant="muted" dot>Não configurado</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <Button variant="outline" size="sm" disabled={status === "not-configured" && actionLabel === "Configurar"}>
        {actionLabel}
      </Button>
    </div>
  );
}

function RoleCard({ role, desc }: { role: string; desc: string }) {
  return (
    <div className="rounded-xl border border-slate-200/60 dark:border-white/10 p-3">
      <p className="font-medium text-sm">{role}</p>
      <p className="text-xs text-muted-foreground">{desc}</p>
    </div>
  );
}

// Avoid linter on unused import (used in JSX above with type signature)
void ShieldCheck;
void Radio;
